import {
  ChangeDetectionStrategy,
  Component,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { fieldsValidators } from '@shared/constants/field-name-validators';

import { InvestmentJurisdictionComboboxComponent } from '@shared/components/investment-jurisdiction-combobox/investment-jurisdiction-combobox.component';
import { Country, EntityType, GridDrawerComponent } from '../../models';
import { EntityTypeInvestmentJurisdiction } from '../../models/entity-type-investment-jurisdiction';
import { InvestmentJurisdictionShort } from '../../models/investment-jurisdiction-short';

@Component({
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'gstar-upsert-entity-type',
  templateUrl: 'upsert-entity-type.component.html',
})
export class UpsertEntityTypeComponent
  extends GridDrawerComponent<EntityType>
  implements OnChanges, OnInit
{
  public readonly maxNameLength = 255;
  enableUpdate: boolean = false;
  isTargetEntityValid: boolean = true;
  isEntityTypeInvJurArrayUnique: boolean = true;
  public form: FormGroup;
  @ViewChild('investmentJurisdictionComp')
  investmentJurisdictionComp!: InvestmentJurisdictionComboboxComponent;

  editingJurisdictions: EntityTypeInvestmentJurisdiction[] = [];

  constructor() {
    const fb = inject(FormBuilder);

    super();
    this.form = fb.group({
      country: null,
      name: [
        '',
        [
          Validators.required,
          Validators.maxLength(this.maxNameLength),
          Validators.pattern(fieldsValidators.letterRequired),
        ],
      ],
      isDefault: false,
      isActive: [{ value: true, disabled: false }],
      entityTypeInvestmentJurisdiction: [],
    });

    this.form.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((values: EntityType) => {
        if (this.isEdit) this.checkChanges(values);
        this.checkUniqueEntityTypeInvestmentJurisdictions();
        this.checkTargetEntity(values);
        values.entityTypeInvestmentJurisdiction = [
          ...this.editingJurisdictions,
        ];
        const isBaseValid =
          this.form.valid &&
          this.isTargetEntityValid &&
          this.isEntityTypeInvJurArrayUnique;

        const isValid = this.isEdit
          ? isBaseValid && this.enableUpdate
          : isBaseValid;
        this.modelChange.emit({
          model: {
            ...this.model,
            ...values,
          },
          prevModel: this.model!,
          isValid,
        });
      });

    this.form.get('isDefault')?.valueChanges.subscribe((value: boolean) => {
      if (value) {
        this.form.get('isActive')?.disable();
      } else {
        this.form.get('isActive')?.enable();
      }
    });
  }

  get nameField(): AbstractControl<string> {
    return this.form.get('name')!;
  }

  get countryValue(): Country {
    return this.form.get('country')?.value;
  }

  get isDefault(): boolean {
    return this.form.get('isDefault')?.value;
  }

  get entityTypeInvestmentJurisdiction(): EntityTypeInvestmentJurisdiction[] {
    return this.form.get('entityTypeInvestmentJurisdiction')?.value;
  }

  ngOnInit(): void {
    this.editingJurisdictions =
      this.model && this.model.entityTypeInvestmentJurisdiction
        ? [...this.model.entityTypeInvestmentJurisdiction]
        : [];

    if (!this.isEdit || !this.isDefault) {
      this.form.get('country')?.addValidators(Validators.required);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const modelChanges = changes['model'];
    if (modelChanges) {
      this.editingJurisdictions =
        modelChanges.currentValue?.entityTypeInvestmentJurisdiction || [];
      this.form.setValue({
        country: modelChanges.currentValue?.country ?? null,
        name: modelChanges.currentValue?.name,
        isDefault: modelChanges.currentValue?.isDefault,
        isActive: !!modelChanges.currentValue?.isActive,
        entityTypeInvestmentJurisdiction: [...this.editingJurisdictions],
      });
    }
  }

  countrySelect(country: Country): void {
    this.form.patchValue({ country, region: country.region });
  }

  clearCountry(): void {
    this.form.patchValue({ country: null });
  }
  invJurisdictionSelect(
    invJurisdiction: InvestmentJurisdictionShort | null | undefined
  ): void {
    if (invJurisdiction?.investmentJurisdictionId) {
      this.editingJurisdictions.push({
        investmentJurisdiction: invJurisdiction,
      });
      this.form
        .get('entityTypeInvestmentJurisdiction')
        ?.setValue([...this.editingJurisdictions]);
    }
  }

  onRemoveJurisdiction(index: number): void {
    this.editingJurisdictions.splice(index, 1);
    this.form
      .get('entityTypeInvestmentJurisdiction')
      ?.setValue([...this.editingJurisdictions]);
  }

  handleTargetEntityChange(index: number, targetEntityType?: EntityType): void {
    this.editingJurisdictions = this.editingJurisdictions.map((item, i) =>
      i === index ? { ...item, targetEntityType } : item
    );
    this.form
      .get('entityTypeInvestmentJurisdiction')
      ?.setValue([...this.editingJurisdictions]);
  }

  private checkChanges(values: EntityType): void {
    if (values && this.model) {
      const hasNameChanged = values.name !== this.model.name;
      const hasCountryChanged =
        values.country?.countryId !== this.model.country?.countryId;
      const hasIsDefaultChanged = values.isDefault !== this.model.isDefault;
      const hasEntityTypeInvJurisdictionChanged =
        JSON.stringify(values.entityTypeInvestmentJurisdiction) !==
        JSON.stringify(this.model.entityTypeInvestmentJurisdiction);
      const hasIsActiveChanged =
        !values.isDefault && values.isActive !== this.model.isActive;
      this.enableUpdate =
        hasNameChanged ||
        hasCountryChanged ||
        hasIsDefaultChanged ||
        hasEntityTypeInvJurisdictionChanged ||
        hasIsActiveChanged;
    }
  }

  private checkTargetEntity(values: EntityType): void {
    this.isTargetEntityValid = values?.entityTypeInvestmentJurisdiction
      ? this.hasEveryTargetEntityType(values.entityTypeInvestmentJurisdiction)
      : true;
  }

  private hasEveryTargetEntityType(
    entityTypeInvestmentJurisdiction: EntityTypeInvestmentJurisdiction[]
  ): boolean {
    return (
      entityTypeInvestmentJurisdiction?.every(
        item => item.targetEntityType?.entityTypeId
      ) ?? false
    );
  }

  private checkUniqueEntityTypeInvestmentJurisdictions(): void {
    this.isEntityTypeInvJurArrayUnique = this.editingJurisdictions
      ? this.hasUniqueEntityTypeInvestmentJurisdictions(
          this.editingJurisdictions
        )
      : true;
  }

  private hasUniqueEntityTypeInvestmentJurisdictions(
    entityTypeInvestmentJurisdiction: EntityTypeInvestmentJurisdiction[]
  ): boolean {
    if (!entityTypeInvestmentJurisdiction.length && !this.isDefault) {
      return false;
    }
    const seen = new Set<string>();
    for (const item of entityTypeInvestmentJurisdiction) {
      const key = JSON.stringify({
        investmentJurisdiction:
          item.investmentJurisdiction?.investmentJurisdictionId,
      });
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
    }
    return true;
  }
}
